import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faEye,
  faEyeSlash
} from '@fortawesome/free-regular-svg-icons';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import useAuth from '../../hooks/useAuth';
import './login.css';
import { BR, US } from 'country-flag-icons/react/3x2';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const languageOptions = {
  pt: { label: 'Português', flag: BR, country: 'Brasil' },
  en: { label: 'English', flag: US, country: 'United States' },
};

const Signin = () => {
  const { signin, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'pt');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef();
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    if (i18n.changeLanguage) {
      i18n.changeLanguage(lang);
    }
    setShowDropdown(false);
  };

  useEffect(() => {
    if (i18n.changeLanguage) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  const CurrentFlag = languageOptions[language]?.flag;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await signin(email, password);
      if (!result.success) {
        setError(result.message);
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError('Erro durante o login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setError('');

    // Limpa logs antigos
    localStorage.removeItem('google_login_logs');

    try {
      const result = await signInWithGoogle();

      if (!result.success) {
        console.error('❌ Login falhou:', result.message);
        setError(result.message);
        setIsGoogleLoading(false);
      } else {
        // Se sucesso, o Supabase redireciona automaticamente para popup do Google
      }
    } catch (err) {
      console.error('❌ Exceção no handleGoogleLogin:', err);
      setError('Erro durante o login com Google: ' + err.message);
      setIsGoogleLoading(false);
    }
  };

  return (
    <div id="login-page">
      <div id="login-container">
        <div id="logo-section">
          <div className="logo-wrapper">
            <span className="logo-text">Wappfy</span>
          </div>
          <p className="logo-subtitle">{t('whatsappApiSubtitle')}</p>
        </div>

        {/* Seletor de idioma */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, position: 'relative' }} ref={dropdownRef}>
          <button
            type="button"
            className="language-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: '#23272f',
              border: '1px solid #4B5563',
              borderRadius: 6,
              padding: '6px 12px',
              color: '#fff',
              fontSize: 15,
              cursor: 'pointer'
            }}
            onClick={() => setShowDropdown(!showDropdown)}
            title={languageOptions[language]?.country}
          >
            {CurrentFlag && <CurrentFlag className="flag-icon" style={{ width: 22, marginRight: 8 }} />}
            <span>{languageOptions[language]?.label}</span>
            <svg style={{ marginLeft: 8, width: 14, height: 14, fill: '#fff' }} viewBox="0 0 20 20"><path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z"/></svg>
          </button>
          {showDropdown && (
            <div
              style={{
                position: 'absolute',
                top: 38,
                left: 0,
                background: '#23272f',
                border: '1px solid #4B5563',
                borderRadius: 8,
                zIndex: 10,
                minWidth: 160,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
              }}
            >
              {Object.entries(languageOptions).map(([code, { label, flag: Flag, country }]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: 15,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    borderBottom: code !== 'en' ? '1px solid #374151' : 'none',
                    fontWeight: code === language ? 700 : 400
                  }}
                  title={country}
                >
                  <Flag className="flag-icon" style={{ width: 22 }} />
                  <span>{label}</span>
                  {code === language && <span style={{ marginLeft: 'auto', color: '#10b981' }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <form id="login-form" onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="input-label">{t('signin.email')}</label>
            <div className="input-wrapper">
              <FontAwesomeIcon 
                icon={faEnvelope} 
                className="input-icon" 
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder={t('signup.placeholderEmail')}
                required
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">{t('signin.password')}</label>
            <div className="input-wrapper">
              <FontAwesomeIcon 
                icon={faLock} 
                className="input-icon" 
              />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || isGoogleLoading}
            className="login-button"
          >
            {isLoading ? (language === 'pt' ? 'Entrando...' : 'Signing in...') : t('signin.login')}
          </button>

          <div className="divider">
            <div className="divider-line"></div>
            <span className="divider-text">{language === 'pt' ? 'ou' : 'or'}</span>
            <div className="divider-line"></div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading || isGoogleLoading}
            className="google-login-button"
          >
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {isGoogleLoading ? t('signin.connecting') : t('signin.continueWithGoogle')}
          </button>

          <div className="additional-links">
            <Link 
              to="/forgot-password"
              className="forgot-password"
            >
              {t('signin.forgotPassword')}
            </Link>
            <div className="signup-text">
              {t('signin.noAccount')}{' '}
              <Link 
                to="/signup"
                className="signup-link"
              >
                {t('signin.createNow')}
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signin;